import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const DISMISS_KEY = "sr_trial_banner_dismissed_v1";
const SHOW_AFTER_QUIZZES = 3;

/**
 * Trial banner that nudges Free users to Premium after they complete
 * a meaningful amount of practice (>=3 quiz attempts). Dismissible per device.
 * Hidden for: paid users, non-students, unauthenticated, or once dismissed.
 */
export default function TrialBanner() {
  const { user, isPremium, isStudent } = useAuth();
  const [show, setShow] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!user) { setShow(false); return; }
    if (!isStudent) { setShow(false); return; }
    // Release 1.2-P3-B — any active paid tier hides the banner.
    if (isPremium) { setShow(false); return; }
    if (typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1") {
      setShow(false); return;
    }
    let cancelled = false;
    const fetchAttempts = () => {
      api.get("/progress").then((r) => {
        if (cancelled) return;
        const n = r.data?.recent_attempts?.length || 0;
        setAttempts(n);
        setShow(n >= SHOW_AFTER_QUIZZES);
      }).catch(() => {});
    };
    fetchAttempts();
    // Refresh when subscription state changes (e.g. right after upgrade).
    const handler = () => fetchAttempts();
    window.addEventListener("sparkread:subscription-changed", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("sparkread:subscription-changed", handler);
    };
  }, [user, isPremium, isStudent]);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <div
      className="relative w-full bg-gradient-to-r from-spark-purple via-[#6E5FFF] to-spark-indigo text-white"
      data-testid="trial-banner"
    >
      <div className="container-spark py-2.5 flex items-center gap-3 flex-wrap">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-white/20 shrink-0">
          <Sparkles size={14} />
        </span>
        <span className="text-sm font-medium leading-snug flex-1 min-w-0">
          <span className="font-bold">{attempts} quizzes in — you&apos;re rolling.</span>
          <span className="hidden sm:inline"> Try <Link to="/daily-spark" className="underline decoration-dotted underline-offset-2 font-bold">Today&apos;s Spark</Link> daily, or unlock the full library, SAT/AP prep, Vocabulary Duel, and unlimited AI coaching.</span>
        </span>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-spark-indigo px-4 py-1.5 text-sm font-bold hover:-translate-y-0.5 transition shrink-0"
          data-testid="trial-banner-cta"
        >
          Go Premium <ArrowRight size={14} />
        </Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="grid h-7 w-7 place-items-center rounded-full hover:bg-white/15 transition shrink-0"
          data-testid="trial-banner-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
